import { LocalizationMessages } from "../../App/Localization"
import { Block, BlockField, BlockRow } from "../../Components/Block"
import { LocalizedMessage } from "../../Components/LocalizedMessage"
import { OverlayScrollBody } from "../../Components/OverlayScrollBody"
import { IShipResyncStatusResponse } from "../../Types/Generated/LogbookApp/ShipApi"

// The bodies of the "Reset disconnected connection" overlay, split out of
// `OverlayContentView`'s dispatch so every state is reachable from props alone --
// the same reasoning that split `CloseShipViews.tsx`. Loading, unreachable,
// claimed-elsewhere, the choice and the confirm step are all states a user can be
// stranded in, and none of them were catalogable while they depended on state private
// to the dispatching component (#1453).

interface ResyncTranslate {
  (key: string): string;
}

// Formats a timestamp for the "which side is more recent" comparison below. The value
// is only ever shown next to its counterpart, so a locale-default rendering is right:
// the user is comparing two of these, not reading an absolute instant.
function formatMoment(value?: string): string | undefined {
  if (!value) {
    return undefined;
  }
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? undefined : parsed.toLocaleString();
}

interface ResyncErrorProps {
  message: string;
}

export function ResyncErrorView({ message }: ResyncErrorProps) {
  return (
    <BlockRow>
      <BlockField className="error-message">{message}</BlockField>
    </BlockRow>
  );
}

interface ResyncStatusViewProps {
  status?: IShipResyncStatusResponse;
  shipName?: string;
  error?: string;
  loading: boolean;
  onResetLocal: () => void;
  onResetCloud: () => void;
  onCancel: () => void;
  t: ResyncTranslate;
}

/**
 * The dialog's landing state, driven entirely by what the CLOUD says rather than by
 * the local mirror of the ship row (#1453).
 *
 * Recovery requires both cloud Primary permission and an unclaimed ship.
 *
 *   * the cloud could not be reached -- say so. Both recoveries are cloud round trips,
 *     so neither can run, and showing a stale local picture as if it were current is
 *     how a user ends up choosing to overwrite the wrong side.
 *   * the cloud still holds a claim -- the ship is open somewhere. The app must not
 *     take it; `resyncUnavailable` walks the user through releasing it in the LOGBOOK
 *     Cloud, after which this dialog offers the recovery.
 *   * the cloud holds nothing -- either direction may be overwritten.
 */
export function ResyncStatusView({ status, shipName, error, loading, onResetLocal, onResetCloud, onCancel, t }: ResyncStatusViewProps) {
  const claimed = status?.claimed === true;
  const permissionDenied = status?.hasPrimaryPermission === false;
  const available = !loading && !error && status != undefined && !claimed && !permissionDenied;
  const explanation = !loading && !error
    ? permissionDenied ? t("resync_permission_required")
      : claimed && status?.claimedByThisDevice ? t("resync_claimed_here") : undefined
    : undefined;
  const cloudMoment = formatMoment(status?.cloudUpdatedAt);
  const localMoment = formatMoment(status?.lastSync);

  return (
    <OverlayScrollBody className="Overlay-body Overlay-body-padding message-text-bigger" readMoreLabel={t("read_more")} deps={[status, error, loading]}>
      <Block>
        <BlockRow>
          {explanation ? <BlockField>{explanation}</BlockField> : (
            <LocalizedMessage
              message={(m: LocalizationMessages) => available ? m.shipInfo.resync : m.shipInfo.resyncUnavailable}
              replace={{ name: shipName || "" }}
            />
          )}
        </BlockRow>
        {error && <ResyncErrorView message={error} />}
        {/* The message above tells the user to "check whether the data here in the
            LOGBOOK app or in the LOGBOOK Cloud is more recent" before choosing which
            side to overwrite -- and until now gave them nothing to check with. */}
        {available && (cloudMoment || localMoment) && (
          <BlockRow>
            <BlockField>
              <div className="Resync-recency">
                {localMoment && <div>{t("resync_last_sync")}: {localMoment}</div>}
                {cloudMoment && <div>{t("resync_cloud_updated")}: {cloudMoment}</div>}
              </div>
            </BlockField>
          </BlockRow>
        )}
        <BlockRow actions={true} className="block-row-one-row">
          <BlockField>
            {/* Overwriting the cloud with nothing is a wipe, not a recovery, so the
                local direction is offered only when this device actually holds data. */}
            <button className="button" onClick={onResetLocal} disabled={!available || status?.hasLocalData !== true}>{t("resync_reset_local")}</button>
          </BlockField>
          <BlockField>
            <button className="button" onClick={onResetCloud} disabled={!available}>{t("resync_reset_cloud")}</button>
          </BlockField>
          <BlockField>
            <button className="button default" onClick={onCancel}>{t("resync_cancel")}</button>
          </BlockField>
        </BlockRow>
      </Block>
    </OverlayScrollBody>
  );
}

interface ResyncConfirmViewProps {
  action: "local" | "cloud";
  shipName?: string;
  busy: boolean;
  error?: string;
  onBack: () => void;
  onConfirm: () => void;
  t: ResyncTranslate;
}

export function ResyncConfirmView({ action, shipName, busy, error, onBack, onConfirm, t }: ResyncConfirmViewProps) {
  return (
    <>
      <div className="Overlay-body Overlay-body-padding message-text-bigger">
        <Block>
          <BlockRow>
            <LocalizedMessage
              message={(m: LocalizationMessages) => m.shipInfo[`resync_reset_${action}_confirm`]}
              replace={{ name: shipName || "" }}
            />
          </BlockRow>
          {/* Without this the dialog was indistinguishable from a dead button: the
              request rejected, and every step after it was skipped in silence. */}
          {error && <ResyncErrorView message={error} />}
        </Block>
      </div>
      <div className="Overlay-footer">
        <Block>
          <BlockRow actions={true} className="block-row-one-row">
            <BlockField>
              <button className="button" onClick={onBack} disabled={busy}>{t("resync_confirm_cancel")}</button>
            </BlockField>
            <BlockField>
              <button className="button default" onClick={onConfirm} disabled={busy}>{t("resync_confirm_execute")}</button>
            </BlockField>
          </BlockRow>
        </Block>
      </div>
    </>
  );
}
